import type {
  CarUsage,
  IncomeFrequencyType,
  VisitReason,
  PolicyStatus,
  HomeOwnership,
  NotInsuredReason,
  AddressDescription,
  AutoInsuredLength,
  CreditRange,
  TrafficSchoolStatus,
  LanguageType,
  EbayUserRoleType,
  ViolationsCountType,
  FaultAccidentType,
  NonFactoryEquipmentNames,
} from '@jerry-serverless/enums';

import type { AddressInput } from './address';

export enum FlowType {
  Default = 'default',
  RepairEstimate = 'repairEstimate',
  // a simple RepairEstimate flow
  RepairShop = 'repairShop',
  CarValue = 'carValue',
  CarRecall = 'carRecall',
  Maintenance = 'maintenance',
}

export interface ShopInsuranceTypes {
  home: boolean;
  renters: boolean;
  umbrella: boolean;
  condo: boolean;
}

export interface ShopInsuranceTypes {
  home: boolean;
  renters: boolean;
  umbrella: boolean;
  condo: boolean;
}

export enum OwnershipEnum {
  Leased = 'Leased',
  Lien = 'Lien',
  Owned = 'Owned',
}

export const INVITED_USER_NAME = 'Invited User';

export type CarUsageEditKey = keyof typeof CarUsage | 'Other';

export type CarAdditionalInfo = {
  // Exisiting damage?
  hasDamage?: boolean;
  // Salvaged or other Branded title?
  hasBrandedTitle?: boolean;
  // What is the value of your vehicle?
  value?: number;
  // Do you have a load capacity of one ton?
  loadCapacityMoreThanOneTon?: boolean;
  // Do you commute to NY or NJ?
  commuteToNYorNJ?: boolean;
  // Do you use your vehicle to carpool?
  carpool?: boolean;
  // Are any vehicles owned by a corporation or a partnership; OR jointly owned by 2 or more individuals who do not reside in the same household?
  sharedOwnership?: boolean;
  // Is this a used car? True indicates the user is not the original owner.
  isUsedCar?: boolean;
  // Do you have any Non-factory equipments?
  nonFactoryEquipments?: Array<{
    // Name of the non-factory equipment
    name: NonFactoryEquipmentNames;
    // Value of the non-factory equipment, dollar in cents
    value: number;
    // Description of the non-factory equipment
    description?: string;
  }>;
  // Where is the vin or partialVin from
  vinSource?: string;
  /** If the car was initially added by YMME and if we defaulted the trim value, this will be true.  */
  isInitialCarDefaultTrim?: boolean;
  // vehicle type
  vehicleType?: VehicleTypes;
  // fuel type
  fuelType?: VehicleFuelTypes;
};

/**
 * Initial car object from user when we pulled no cars.
 */
export interface InitialCar {
  id?: string;
  year?: string | null;
  make?: string | null;
  model?: string | null;
  submodel?: string | null;
  vin?: string | null;
  partialVin?: string | null;
  usage?: CarUsageEditKey | null;
  ridesharing?: boolean | null;
  annualMileage?: number | null;
  usedForDelivery?: boolean | null;
  business?: boolean | null;
  licensePlateState?: string | null;
  licensePlateNumber?: string | null;
  ownership?: OwnershipEnum | null;
  additionalInfo?: CarAdditionalInfo | null;
}

export interface Income {
  amount?: number | null;
  averageHoursWeekly?: number | null;
  frequency?: IncomeFrequencyType | null;
}

export enum SignupCurrentCoverage {
  StateMinimum = 'StateMinimum',
  Silver = 'Silver',
  Gold = 'Gold',
  More = 'More',
  IDK = 'IDK',
}

export enum SignupCoveragePreference {
  StateMinimum = 'StateMinimum',
  Advisor = 'Advisor',
  Current = 'Current',
  Customize = 'Customize',
}

export enum CoverageAdvisorNetAssets {
  LessThan50k = 'LessThan50k',
  Between50kAnd100k = 'Between50kAnd100k',
  Between100kAnd300k = 'Between100kAnd300k',
  Between300kAnd500k = 'Between300kAnd500k',
  Between500kAnd1M = 'Between500kAnd1M',
  MoreThan1M = 'MoreThan1M',
  PreferNotToSay = 'PreferNotToSay',
}

export enum CoverageAdvisorDeductible {
  LessThan500 = 'LessThan500',
  Around1000 = 'Around1000',
  MoreThan1000 = 'MoreThan1000',
}

export interface CoverageAdvisor {
  netAssets?: CoverageAdvisorNetAssets;
  deductible?: CoverageAdvisorDeductible;
  rental?: boolean;
  towing?: boolean;
  /**
   * User's preference of full coverage for each car. Key is gloabl UserCar ID.
   * If a user's car is missing from this map, it means the user did not make a choice for that car.
   */
  fullCoverage?: Record<string, boolean>;
}

export interface CustomCoverage {
  assetID: string;
  ciName: string;
  ciValue: string;
}

export interface SignupParams {
  /**
   * OS of the app that the user uses.
   */
  appOS?: boolean;

  /**
   * utm_source when signup
   */
  utmSource?: string;

  /**
   * Addresses found for user by identity engine.
   */
  addresses: AddressInput[];
  /**
   * TODO: What is this?
   */
  accidents: string[];
  /**
   * Indicates whether onboarding has completed.
   */
  validated: boolean;
  /**
   * The date string when onboarding was completed. Note that only users after 2024-10-03 would have this field.
   * @example "2024-10-03T01:23:45.000Z"
   */
  validatedAt?: string;
  /**
   * User specified car insurance premium.
   */
  enteredPremium?: number;
  /**
   * User specified car insurance term.
   */
  premiumTerm?: number;
  /**
   * User specified home insurance premium.
   */
  enteredHomePremium?: number;
  /**
   * User specified home insurance term.
   */
  homePremiumTerm?: number;
  /**
   * DEPRECATED: Last saved step in the signup funnel.
   */
  lastStep?: string;
  /**
   * DEPRECATED: Furthest saved step in the signup funnel.
   */
  furthestStep?: string;
  /**
   * User specified they have claims.
   */
  claims?: boolean;

  /**
   * User specified fault accident.
   */
  faultAccident?: FaultAccidentType | null;

  /**
   * User specified they have violations.
   */
  violations?: boolean;

  /**
   * User specified their violation count.
   */
  violationsCount?: ViolationsCountType | null;

  /**
   * User don't know violations
   */
  violationsNotSure?: boolean;

  /**
   * User specified they have auto insurance.
   */
  isAutoInsured?: boolean;
  /**
   * User specified reason for not being insured.
   */
  notInsuredReason?: NotInsuredReason;
  /**
   * Length of user insured with current carrier.
   */
  autoInsuredLength?: AutoInsuredLength;
  /**
   * Length of continuous insured with all carriers.
   */
  continuousCoverageLength?: number;
  /**
   * User modified coverage items, i.e., did not use imported policy as is.
   */
  useCustomPolicy?: boolean;
  /**
   * Policy import completed.
   * NOTE: What is this?
   */
  donePolicyLoading?: boolean;
  /**
   * Status of imported policy.
   */
  policyStatus?: PolicyStatus;
  /**
   * Name of carrier in imported policy.
   */
  policyCarrier?: string;
  /**
   * User changed carrier in imported policy.
   */
  isAutoCarrierChanged?: boolean;
  /**
   * Coverage items are predicted by Jerry ML service.
   */
  isPredicted?: boolean;
  /**
   * Predicted coverage items were modified by user.
   * NOTE: This seems redundant. `useCustomPolicy` and `isPredicted` should be enough to store
   * this piece of information.
   */
  predictedEdited?: boolean;
  /**
   * Showing of `Shop For` screen was enabled for this user.
   */
  shopForEnabled: boolean;
  /**
   * User specified which insurance types to shop for.
   */
  shopFor: ShopInsuranceTypes;
  /**
   * User changed the date of birth we found.
   */
  dobChanged?: boolean;
  /**
   * User manually entered their address.
   */
  addressChanged?: boolean;
  /**
   * Payfone search performed.
   */
  pf?: boolean;
  /**
   * User specified reason for visit.
   */
  visitReason?: VisitReason;

  /**
   * Enabled features
   */
  reorderSignUpEnabled: boolean;

  /**
   * User account was created using partner API.
   */
  partnerSignup: boolean;
  /**
   * Specifies funnel steps that should be skipped.
   * NOTE: This is specificall for accounts created using partner API.
   * We want to skip certain steps in the funnel that we already have data for.
   */
  skipSteps: string[];
  /**
   * When selecting other in referral, user input the name
   */
  referralOther?: string;

  /**
   * TODO: what is this?
   */
  irClickID?: string;

  /**
   * Telematics flag for user.
   */
  telematics?: boolean;

  /**
   * Vehicle Ownership page enablement.
   */
  askVehicleOwnership: boolean;

  /**
   * Show 'Where did you hear about us' page.
   */
  askReferral?: boolean;

  /**
   * Home ownership value.
   */
  homeOwnership?: HomeOwnership;

  /**
   * Refi questions
   */
  hasCarLoan?: boolean;

  hasMinNumberCarLoanPayments?: boolean | null;

  carLoanBalance?: number;

  carLoanMonthlyPayment?: number;

  income?: Income;

  /**
   * User's initial car
   */
  initialCar?: InitialCar;

  /**
   * User seleted credit range in onboarding flow
   */
  creditRange?: CreditRange;

  /**
   * Indicates that the user is interested in the insurance shopping service
   */
  insuranceService?: boolean | undefined;

  /**
   * Indicates that the user is interested in the garage service
   */
  garageService?: boolean | undefined;

  /**
   * Indicates that the user is interested in the dwep service
   */
  dwepService?: boolean | undefined;

  /**
   * Indicates that the user is interested in the refi service
   */
  refiService?: boolean | undefined;

  /**
   * Indicates that the user is interested in ebay service
   */
  eBayService?: boolean | undefined;

  /**
   * Indicates that what role the user is in transaction.
   */
  eBayUserRole?: EbayUserRoleType | undefined;

  eBayCar?: Partial<{
    vin: string;
    year: string;
    make: string;
    model: string;
    engine: string;
    phone: string;
  }>;

  eBayTransactionID?: string;

  /**
   * Records the user status in traffic school flow
   */
  trafficSchoolStatus?: TrafficSchoolStatus;

  /**
   * Indicates that if user's current App version supports household member.
   */
  bindingHhmEnabled?: boolean | undefined;

  /**
   * Records the language type during signup/onboarding
   */
  languageType?: LanguageType;

  /**
   * A reasonably accurate estimate of a ReFi applicant's vehicle mileage to determine if the applicant can pre-qualify
   * for a refinancing and provide accurate initial offers.
   */
  estimatedMileage?: number;

  /**
   * If user go through lite onboarding
   */
  isOnboardingLite?: boolean;

  /**
   * User from invite version
   */
  fromInviteVersion?: string;

  /**
   * When generated the refi initial offers
   */
  initialOfferCheckedAt?: string | null;

  /**
   * User created by partner prefill quoting confirmed information
   */
  partnerSignupConfirmed?: boolean;

  /**
   * If the user force to use business address
   */
  addressBusiness?: boolean;

  /**
   * User specified reason for not being insured.
   */
  addressDescription?: AddressDescription;

  /**
   * The mark of user selects whether or not they need the NNO insurance.
   * Please note that this is only used to indicate the user's choice during the signup phase,
   * and the user can still add a vehicle in the app after completing signup.
   */
  needNNOInsurance?: boolean;

  /**
   * Ask user if there is vehicle registered in their household's name.
   */
  hasHouseholdVehicle?: boolean;

  /**
   * The tier the user selected for bodily injury coverage.
   */
  bodilyInjuryTier?: number;

  /**
   * What’s closest to your current or most recent coverage?
   */
  currentCoverage?: SignupCurrentCoverage;

  /**
   * How would you like to choose your coverage?
   */
  coveragePreference?: SignupCoveragePreference;

  /**
   * Responses to coverage advisor questions.
   */
  coverageAdvisor?: CoverageAdvisor;

  /**
   * Custom coverages.
   */
  customCoverages?: CustomCoverage[];

  /**
   * Specifies the car IDs that have full coverage.
   */
  carsWithFullCoverage?: string[];

  // User signed up from garage only flow
  flowType?: FlowType;

  nameVariation?: Record<string, string>;

  repairJob?: {
    id: number;
    choices?: { id: number }[];
    qnas?: { key: string; a: string }[];
  };

  // Whether we have found car recalls or not
  hasCarRecalls?: boolean;

  // Records the user has ever translated the page to a different language.
  translatedTo?: string;
}

/**
 * Jerry vehicle types base NHTSA, and include some ITC vehicle types
 * ITC vehicle types:
 *    Truck/Car/Station Wagon/Utility/Mini Van/Van
 * NHTSA vehicle types(https://crashstats.nhtsa.dot.gov/#!/PublicationList/133):
 *    Bus / Incomplete Vehicle / Low Speed Vehicle (LSV) / Motorcycle / Multipurpose Passenger Vehicle (MPV) / Off Road Vehicle / Passenger Car / Trailer /Truck
 *  1. Passenger Car and Station Wagon one of the Car, separate Station Wagon from car
 *  2. Utility/Mini Van/Van one of the MVP, but here we separate them
 */
export enum VehicleTypes {
  Truck = 'Truck',
  StationWagon = 'Station Wagon', // Car - Station Wagon
  Car = 'Car', // general Passenger Car
  Utility = 'Utility', // MPV - SUV
  MiniVan = 'MiniVan', // MPV - MiniVan
  Van = 'Van', // MPV - VAN
  MPV = 'Multipurpose Passenger Vehicle (MPV)', // general MPV
  Bus = 'Bus',
  Motorcycle = 'Motorcycle',
  Trailer = 'Trailer',
  IncompleteVehicle = 'Incomplete Vehicle',
  LSV = 'Low Speed Vehicle (LSV)',
  OffRoadVehicle = 'Off Road Vehicle',
}

// Jerry vehicle types base ITC
export enum VehicleFuelTypes {
  Gasoline = 'Gasoline', // Regular petrol
  Diesel = 'Diesel', // Diesel fuel
  Electric = 'Electric', // Battery electric vehicle
  Hybrid = 'Hybrid', // Gasoline/Diesel + Electric
  Flexible = 'Flexible', // Flexible fuel vehicle (e.g. E85)
  Ethanol = 'Ethanol', // Ethanol-based fuel (E85, E100)
  Methanol = 'Methanol', // Methanol-based fuel (M85, M100)
  NaturalGas = 'Natural Gas', // CNG, LNG
  Propane = 'Propane', // LPG / liquefied petroleum gas
}
